import { PanelResolveType, type PageRef } from '@wix/editor-platform-sdk-types'
import {
  cmsNotificationSrc83Evid11,
  sitePagesMenuInteractionsSrc83Evid9,
  cmUrlSlugErrorMessage,
} from '@wix/bi-logger-wix-code-data/v2'
import {
  EDITOR_TYPE,
  getPageTypeFromPattern,
  type AppApi,
} from '@wix/wix-data-client-common'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type { HttpClient } from '@wix/http-client'
import type {
  EditorSDK,
  I18n,
  Logger,
} from '@wix/wix-data-client-common-standalone'
import {
  getUrlParts,
  openConfirmPatternChangePanel,
  showSuccessToastNotification,
  showUrlChangeErrorPanel,
} from './helpers'
import { saveUrlInLiveSite } from './saveUrlInLiveSite'
import { pageTypeToBiValue } from '../../business-logic/dynamic-pages/pageTypeToBiValue'

interface Params {
  appApi: AppApi
  editorSdkProxy: EditorSDK
  i18n: I18n
  httpClient: HttpClient
  logger: Logger
  pageRef: PageRef
  collectionId: string
  newUrl: string
  collectionsApi: CollectionsApi
}

export const saveInEditDynamicPageUrlStructurePanel = async ({
  appApi,
  editorSdkProxy,
  i18n,
  logger,
  httpClient,
  pageRef,
  collectionId,
  newUrl,
  collectionsApi,
}: Params) => {
  const [
    pageCount,
    isSitePublished,
    { displayName: collectionName },
    editorType,
    { previousPrefix, previousPattern, newPrefix, newPattern },
  ] = await Promise.all([
    appApi.getDynamicPageUrlCount({ pageRef }),
    editorSdkProxy.info.isSitePublished(),
    collectionsApi.get(collectionId),
    appApi.getEditorType(),
    getUrlParts({ editorSdkProxy, pageRef, newUrl }),
  ])

  const pageCountNormalized = pageCount ?? 0
  const environment = editorType === EDITOR_TYPE.STUDIO ? 'studio' : 'editor'

  const sendSitePagesMenuInteractionsBI = ({
    panel_name,
    action,
  }: {
    panel_name: string
    action: string
  }) => {
    logger.bi(
      sitePagesMenuInteractionsSrc83Evid9({
        panel_name,
        pageId: pageRef.id,
        action,
        collectionId,
        environment,
        pageType: pageTypeToBiValue(getPageTypeFromPattern(newUrl)),
        num_of_items: pageCountNormalized,
        urlSlug: newUrl,
      }),
    )
  }

  const existingPagelinksRegenerated =
    pageCountNormalized > 0 && isSitePublished
  if (existingPagelinksRegenerated) {
    const panelResult = await openConfirmPatternChangePanel({
      editorSdkProxy,
      i18n,
      pageCount: pageCountNormalized,
    })

    const resultToBiAction = {
      [PanelResolveType.MAIN_ACTION]: 'replace_url',
      [PanelResolveType.SECONDARY_ACTION]: 'cancel',
      [PanelResolveType.CLOSE_ACTION]: 'close',
    }

    sendSitePagesMenuInteractionsBI({
      panel_name: 'confirm-pattern-change-panel',
      action: resultToBiAction[panelResult],
    })

    if (panelResult !== PanelResolveType.MAIN_ACTION) {
      return { shouldClosePanel: false }
    }
  }

  try {
    await appApi.saveNewDynamicPageUrl({
      pageRef,
      collectionId,
      newUrl,
    })
  } catch (error) {
    logger.error(
      new Error('Failed to save new dynamic page url', { cause: error }),
    )
    logger.bi(
      cmUrlSlugErrorMessage({
        collection_id: collectionId,
        error_type: 'update_error',
        origin: 'edit_url_slug',
        environment,
        collection_name: collectionName,
        conflictType: 'alert',
        componentType: 'notification_banner',
      }),
    )
    const response = await showUrlChangeErrorPanel({
      editorSdkProxy,
      i18n,
    })
    if (response === PanelResolveType.SECONDARY_ACTION) {
      editorSdkProxy.editor.openHelpPanel({
        helpId: '0bdfcec6-a05e-4604-be55-cacd7e912259',
      })
    }
    return { shouldClosePanel: false }
  }

  if (pageCountNormalized > 0) {
    showSuccessToastNotification({
      editorSdkProxy,
      appApi,
      i18n,
      pageCount: pageCountNormalized,
      shouldShowLink: isSitePublished,
    })
    logger.bi(
      cmsNotificationSrc83Evid11({
        collectionId,
        pageId: pageRef.id,
        urlSlug: newUrl,
        num_of_items: pageCountNormalized,
        collection_name: collectionName,
      }),
    )
  }

  if (isSitePublished) {
    saveUrlInLiveSite({
      httpClient,
      editorSdkProxy,
      i18n,
      logger,
      pageId: pageRef.id,
      previousPrefix,
      previousPattern,
      newPrefix,
      newPattern,
      sendSitePagesMenuInteractionsBI,
    })
  }

  return { shouldClosePanel: true }
}
